import React, {useContext, useEffect, useState} from 'react'
import CloseIcon from '@material-ui/icons/Close';
import {Checkbox, Container, TextField, Typography, FormControlLabel} from '@material-ui/core'
import clsx from 'clsx'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import * as yup from 'yup'
import {FormikProvider, useFormik} from 'formik'
import {Link} from 'react-router-dom'
import I18nContext from '../../../services/I18n/I18nContext'
import useStyles from './FormStyles'
import {ActionData, QuoteAction, QuoteContext} from '../../../services/quote/QuoteProvider'
import DeliveryAddressForm from './components/DeliveryAddressForm'
import {getGoogleTagItems, validate} from '../../../utils/helpers/helpers'
import {currency} from '../Services/components/ServiceItem';
import {useIsMount} from '../../../utils/hooks/useIsMount';

const phoneRegExp = /^(\+)(3)(8)(5)\d{6,11}?$/
const partnerCodeRegExp = /^[0-9]*$/

const formInit = {
    oib: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    address: '',
    place: '',
    zip: '',
    deliveryOib: '',
    deliveryFirstName: '',
    deliveryLastName: '',
    deliveryCompany: '',
    deliveryPhone: '',
    deliveryEmail: '',
    deliveryAddress: '',
    deliveryPlace: '',
    deliveryZip: '',
    partnerCode: '',
    partnerCodeChecked: false,
    deliveryAddressChecked: false,
    terms: true,
    informed: true
}

const Form = () => {

    const classes = useStyles()
    const {translate} = useContext(I18nContext)
    const {dispatch, quote} = useContext(QuoteContext)
    const [isOibValid, setIsOibValid] = useState(quote.isFormValid)
    const isMount = useIsMount();

    const formSchema = yup.object().shape({
        oib: yup.string().required(translate('requiredFiled')),
        firstName: yup.string().required(translate('requiredFiled')),
        lastName: yup.string().required(translate('requiredFiled')),
        company: yup.string().required(translate('requiredFiled')).max(42, translate('maxCharCompany')),
        phone: yup.string().min(10, translate('phoneValidation')).matches(phoneRegExp, translate('phoneValidation')),
        email: yup.string().email(translate('invalidEmail')).required(translate('requiredFiled')),
        address: yup.string().required(translate('requiredFiled')),
        place: yup.string().required(translate('requiredFiled')),
        zip: yup.number().required(translate('requiredFiled')).min(1, translate('requiredFiled')).typeError(translate('requiredFiled')),
        deliveryAddressChecked: yup.bool(),
        partnerCodeChecked: yup.bool(),
        partnerCode: yup.string().when('partnerCodeChecked', {
            is: true,
            then: s => s.required(translate('partnerCodeValidation')).min(5, translate('partnerCodeValidation')).max(6, translate('partnerCodeValidation')).matches(partnerCodeRegExp, translate('partnerCodeValidation')),
        }),
        terms: yup.bool().oneOf([true], 'Must agree to something'),
        informed: yup.bool(),
        deliveryFirstName: yup.string().when('deliveryAddressChecked', {
            is: true,
            then: s => s.required(translate('requiredFiled'))
        }),
        deliveryLastName: yup.string().when('deliveryAddressChecked', {
            is: true,
            then: s => s.required(translate('requiredFiled'))
        }),
        deliveryPhone: yup.string().min(10, translate('phoneValidation')).matches(phoneRegExp, translate('phoneValidation')).when('deliveryAddressChecked', {
            is: true,
            then: s => s.required(translate('requiredFiled')),
        }),
        deliveryAddress: yup.string().when('deliveryAddressChecked', {
            is: true,
            then: s => s.required(translate('requiredFiled'))
        }),
        deliveryPlace: yup.string().when('deliveryAddressChecked', {
            is: true,
            then: s => s.required(translate('requiredFiled'))
        }),
        deliveryZip: yup.number().when('deliveryAddressChecked', {
            is: true,
            then: s => s.required(translate('requiredFiled')).min(2, translate('requiredFiled')).typeError(translate('requiredFiled')),
        }),
    }).test(
        'oibValid',
        'Oib not valid',
        (values) => {
            let deliveryOibValid = false
            const oibValid = validate(values.oib || '')
            setIsOibValid(oibValid)

            return oibValid && (values.deliveryAddressChecked ? deliveryOibValid : true)
        })

    const formik = useFormik({
        initialValues: quote.form || formInit,
        validationSchema: formSchema,
        onSubmit: values => {
            console.log(values)
        }
    })

    const {
        values,
        handleChange,
        handleSubmit,
        errors,
        touched,
        isValid,
        dirty,
        handleBlur,
        setFieldValue,
        setFieldTouched
    } = formik

    useEffect(() => {
        if(!isMount) return;
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: "add_to_cart",
            ecommerce: {
                currency: currency,
                value: quote.totalFixed === 0 ? quote.totalMonthly : quote.totalFixed,
                items: getGoogleTagItems(quote)
            }
        })
        // eslint-disable-next-line
    }, [isMount])

    useEffect(() => {
        dispatch({
            type: QuoteAction.setForm,
            data: {form: values} as ActionData
        })
        // eslint-disable-next-line
    }, [values])


    useEffect(() => {
        dispatch({
            type: QuoteAction.setIsFormValid,
            data: {isFormValid: (quote.formDirty ? isValid : (dirty && isValid)) && isOibValid} as ActionData
        })
        // eslint-disable-next-line
    }, [isValid, isOibValid])

    return (
        <Container className={classes.root}>
            <div className={classes.header}>
                <Link to='/' className={classes.backButton}>
                    <ArrowBackIosIcon className={classes.backButtonIcon}/>
                    {translate('back')}
                </Link>
                <h1 className={classes.rootTitle}>{translate('formTitle')}</h1>
            </div>

            <div className={classes.card}>
                <Typography className={classes.h3} variant='h3'>{translate('formSubtitle')}</Typography>
                <FormikProvider value={formik} key='stef'>
                    <form onSubmit={handleSubmit} name='quoteForm' className={classes.formWrapper}>
                        <div className={classes.row}>
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='oib'
                                onBlur={handleBlur}
                                label={`${translate('oib')} *`}
                                placeholder={translate('oib')}
                                value={values.oib}
                                onChange={handleChange}
                                error={touched.oib && (Boolean(errors.oib) || !isOibValid)}
                                helperText={touched.oib && errors.oib}
                            />
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='firstName'
                                onBlur={handleBlur}
                                label={`${translate('firstName')} *`}
                                placeholder={translate('firstName')}
                                value={values.firstName}
                                onChange={handleChange}
                                error={touched.firstName && Boolean(errors.firstName)}
                                helperText={touched.firstName && errors.firstName}
                            />
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='lastName'
                                label={`${translate('lastName')} *`}
                                placeholder={translate('lastName')}
                                onBlur={handleBlur}
                                value={values.lastName}
                                onChange={handleChange}
                                error={touched.lastName && Boolean(errors.lastName)}
                                helperText={touched.lastName && errors.lastName}
                            />
                        </div>
                        <div className={classes.row}>
                            <TextField
                                className={clsx(classes.input, classes.doubleInput)}
                                variant='outlined'
                                name='company'
                                onBlur={handleBlur}
                                label={`${translate('company')} *`}
                                placeholder={translate('company')}
                                value={values.company}
                                onChange={handleChange}
                                error={touched.company && Boolean(errors.company)}
                                helperText={touched.company && errors.company}
                            />
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='phone'
                                placeholder={translate('phonePlaceholder')}
                                onBlur={handleBlur}
                                label={`${translate('phone')} *`}
                                value={values.phone}
                                onChange={handleChange}
                                error={Boolean(errors.phone)}
                                helperText={errors.phone}
                            />
                        </div>
                        <div className={classes.row}>
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='email'
                                onBlur={handleBlur}
                                label={`${translate('email')} *`}
                                placeholder={translate('email')}
                                value={values.email}
                                onChange={handleChange}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='address'
                                onBlur={handleBlur}
                                label={`${translate('address')} *`}
                                placeholder={translate('address')}
                                value={values.address}
                                onChange={handleChange}
                                error={touched.address && Boolean(errors.address)}
                                helperText={touched.address && errors.address}
                            />
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='place'
                                onBlur={handleBlur}
                                label={`${translate('place')} *`}
                                placeholder={translate('place')}
                                value={values.place}
                                onChange={handleChange}
                                error={touched.place && Boolean(errors.place)}
                                helperText={touched.place && errors.place}
                            />
                        </div>
                        <div className={classes.row}>
                            <TextField
                                className={classes.input}
                                variant='outlined'
                                name='zip'
                                onBlur={handleBlur}
                                inputProps={{
                                    minLength: 2
                                }}
                                label={`${translate('zip')} *`}
                                placeholder={translate('zip')}
                                value={values.zip}
                                onChange={handleChange}
                                error={touched.zip && Boolean(errors.zip)}
                                helperText={touched.zip && errors.zip}
                            />
                        </div>
                        <div className={classes.checkboxWrapper} style={{marginTop: '30px', paddingLeft: 10}}>
                            <FormControlLabel
                                control={
                                    <Checkbox value={values.deliveryAddressChecked}
                                              onChange={handleChange}
                                              name='deliveryAddressChecked'
                                              defaultChecked={values.deliveryAddressChecked}
                                              color='primary'/>
                                }
                                classes={{ label: classes.labelBold }}
                                label={translate('deliveryAddress')}
                            />
                        </div>
                        {values.deliveryAddressChecked && (
                            <div className={classes.deliveryAddressFormWrapper}>
                                <DeliveryAddressForm formik={formik} />
                            </div>
                        )}
                        <Typography className={classes.h3} variant='h3'>{translate('consent')}</Typography>
                        <div className={classes.checkboxWrapper}>
                            <Checkbox value={values.terms} onChange={handleChange} name='terms' color='primary'
                                      defaultChecked={true}/>
                            <div className={classes.fullRow}>
                                <Typography className={classes.label}>{translate('accept')}</Typography>
                                <a href='https://www.neosalon.hr/uvjeti/' className={classes.boldLink} target='_blank'
                                   rel='noreferrer'>
                                    {translate('terms')}
                                </a>
                                <Typography className={classes.labelRequired}>*</Typography>
                            </div>
                        </div>
                        <div className={classes.checkboxWrapper} style={{paddingLeft: 10}}>
                            <FormControlLabel
                                control={
                                    <Checkbox value={values.informed}
                                              defaultChecked={true}
                                              onChange={handleChange}
                                              name='informed'
                                              color='primary'/>
                                }
                                label={translate('informed')}
                            />
                        </div>
                        <div className={classes.fullRow}>
                            <Typography className={classes.label}>
                                {translate('privacyInfo')}
                                <span>
                                    <a href='https://www.neosalon.hr/pravilnik-o-privatnosti-podataka/'
                                          className={classes.link} target='_blank' rel='noreferrer'>
                                        {translate('here')}
                                    </a>
                                </span>
                            </Typography>
                        </div>
                        <Typography className={classes.h3} variant='h3'>{translate('partners')}</Typography>
                        <div className={classes.partnerWrapper}>
                            <div className={classes.row}>
                                <Typography className={classes.label}>{translate('partnerMessage')}</Typography>
                                <Typography className={clsx(classes.link, classes.partnerLink)}
                                            onClick={() => setFieldValue('partnerCodeChecked', true)}>{translate('here')}</Typography>
                            </div>
                            {values.partnerCodeChecked && (
                                <div className={classes.partnerCodeWrapper}>
                                    <TextField
                                        className={classes.input}
                                        variant='outlined'
                                        name='partnerCode'
                                        onBlur={handleBlur}
                                        label={translate('partnerCode')}
                                        value={values.partnerCode}
                                        onChange={handleChange}
                                        error={touched.partnerCode && Boolean(errors.partnerCode)}
                                        helperText={touched.partnerCode && errors.partnerCode}
                                    />
                                    <CloseIcon className={classes.removeButton} onClick={() => {
                                        setFieldValue('partnerCodeChecked', false)
                                        setFieldValue('partnerCode', '')
                                        setFieldTouched('partnerCode', false)
                                    }}/>
                                </div>
                            )}                          
                        </div>
                    </form>
                </FormikProvider>
                <div className={classes.infoWrapper}>
                    <Typography className={classes.infoMessage}>
                        {translate('formInfoMessage')}
                    </Typography>
                </div>
            </div>
        </Container>
    )
}

export default Form
